/*======================================================================================================================
  * Bluebell: src/app/state/postpro/postpro.reducer.ts
  * Last Edited: 14/07/2025
  * Copyright 2025 Bramble CFD LTD
  * The contents of this file are NOT for redistribution
  * See AUTHORS for list of developers on this project
 =====================================================================================================================*/
import { constructCameraSet } from '@app/components/postpro-viewer/postpro-viewer.service';
import { createEntityAdapter, EntityState } from '@ngrx/entity';
import { createReducer, on } from '@ngrx/store';
import { programmeProjectSelectionUnapplied } from '@state/account/account.actions';
import { deselectAllResults } from '@state/results/results.actions';
import { listImageDeltasSuccess } from '@state/image-deltas/image-deltas.actions';
import { ImageDelta } from '@state/image-deltas/image-deltas.interfaces';
import {
  clearPostproAvailableVariables,
  postproViewerClosed,
  deselectPostproVariable,
  listPostproAvailableVariables,
  listPostproAvailableVariablesFailure,
  listPostproAvailableVariablesSuccess,
  loadPostproReferences,
  loadPostproReferencesFailure,
  loadPostproReferencesSuccess,
  reselectLoadedReferences,
  selectPostproVariable,
  selectGridOverlay,
  cameraClick,
  setSelectedCameras,
  toggleAllowMultipleCameras,
  setPlotOverlayReferences,
  setPlotOverlayClipping,
  setPlotOverlaySide,
  clearPlotOverlaySettings,
  clearPlotOverlayReferences,
  loadLICReferencesSuccess, clearLICReferences,
} from '@state/postpro/postpro.actions';
import {
  Postpro,
  PostproAvailableVariables,
  AllPostproType,
  PostproReference,
  PlotOverlayClipping,
  PlotOverlaySide,
} from '@state/postpro/postpro.interfaces';


export interface State extends EntityState<Postpro> {
  // References
  selectedVariables: string[];
  selectedType: AllPostproType|null;
  selectedCameras: string[];
  allowMultipleCameras: boolean;
  loadedVariables: string[];
  referencesLoading: boolean;
  // Available Postpro
  availableVariables: PostproAvailableVariables|null;
  availableVariablesLoading: boolean;
  // Misc
  gridOverlay: string;
  plotOverlayClipping: PlotOverlayClipping|null;
  plotOverlaySide: PlotOverlaySide|null;
  plotOverlayReferences: PostproReference[];
  licReferences: PostproReference[];
}

export const adapter = createEntityAdapter<Postpro>({
  selectId: (postpro: Postpro) => postpro.path,
});

export const initialState: State = adapter.getInitialState<State>({
  selectedVariables: [],
  loadedVariables: [],
  selectedType: null,
  selectedCameras: [],
  allowMultipleCameras: false,
  referencesLoading: false,
  availableVariables: null,
  availableVariablesLoading: false,
  gridOverlay: 'Grid',
  plotOverlayClipping: null,
  plotOverlaySide: null,
  plotOverlayReferences: [],
  licReferences: [],
});

export const reducer = createReducer(
  initialState,

  on(programmeProjectSelectionUnapplied, () => {
    return adapter.removeAll({ ...initialState });
  }),

  /**
   * Variables
   */

  // When a new variable is selected, if it has a different type then clear the
  // references and variables state before it moves to the effects
  on(selectPostproVariable, (state, { postproType, camera }) => {
    if ((state.selectedType && state.selectedType !== postproType) || (camera && !state.selectedCameras.includes(camera))) {
      return adapter.removeAll({
        ...state,
        selectedVariables: [],
        loadedVariables: [],
        selectedType: postproType,
        selectedCameras: [],
      });
    } else {
      return {
        ...state,
        selectedType: postproType,
      };
    }
  }),

  on(deselectPostproVariable, (state, { variable, postproType, camera }) => {
    if (['plot', 'check', 'movie'].includes(postproType)) {
      let selectedCameras = state.selectedCameras;

      if (camera && state.selectedVariables.length === 1) {
        selectedCameras = selectedCameras.filter(selectedCamera => selectedCamera !== camera);
      }
      return {
        ...state,
        selectedVariables: state.selectedVariables.filter(selected => selected !== variable),
        selectedCameras,
      };
    } else {
      return state;
    }
  }),

  on(postproViewerClosed, (state) => {
    return adapter.removeAll({
      ...state,
      selectedVariables: [],
      loadedVariables: [],
      selectedType: null,
      selectedCameras: [],
      allowMultipleCameras: false,
      gridOverlay: 'Grid',
      plotOverlayClipping: null,
      plotOverlaySide: null,
      plotOverlayReferences: [],
      licReferences: [],
    });
  }),

  /**
   * References
   */

  on(loadPostproReferences, (state) => {
    return {
      ...state,
      referencesLoading: true
    };
  }),

  on(loadPostproReferencesSuccess, (state, { references, variable, camera }) => {
    const cameraSet = constructCameraSet<Postpro>(references);
    const selectedCamerasSet = new Set(state.selectedCameras);
    let selectedCameras: string[] = [];

    if (camera) {
      // If there is a camera passed (for movies) then set it as the current camera
      selectedCameras = [camera];
    } else if (selectedCamerasSet.size) {
      // If there are still cameras selected (adding new variables to current type) then maintina the current selections
      selectedCameras = [...selectedCamerasSet];
    } else if (cameraSet.length) {
      // otherwise if there is nothing current selected but there are some available, set a default selection
      selectedCameras = [cameraSet[0]];
    }
      // Otherwise the empty list will be used


    return adapter.upsertMany(
      references,
      {
        ...state,
        referencesLoading: false,
        selectedVariables: [...new Set([...state.selectedVariables, variable ])],
        loadedVariables: [...new Set([...state.loadedVariables, variable])],
        selectedCameras,
      }
    );
  }),

  on(listImageDeltasSuccess, (state, { data }) => {
    const cameraSet = constructCameraSet<ImageDelta>(data);
    return {
      ...state,
      selectedCameras: cameraSet.length ? [cameraSet[0]] : []
    };
  }),

  on(loadPostproReferencesFailure, (state, { variable, camera }) => {
    return {
      ...state,
      selectedVariables: state.selectedVariables.filter(selected => selected !== variable),
      selectedCameras: state.selectedCameras.filter(selectedCamera => selectedCamera !== camera),
      referencesLoading: false,
    };
  }),

  // When a variable is selected that is already selected then simply add it back into the selected variables
  on(reselectLoadedReferences, (state, { variable, camera }) => {
    return {
      ...state,
      selectedVariables: [...new Set([...state.selectedVariables, variable])],
      selectedCameras: camera ? [...state.selectedCameras, camera] : state.selectedCameras,
    };
  }),

  /**
   * Misc
   */

  on(listPostproAvailableVariables, (state) => {
    return {
      ...state,
      availableVariablesLoading: true,
    };
  }),

  on(listPostproAvailableVariablesSuccess, (state, { availableVariables }) => {
    return  {
      ...state,
      availableVariables,
      availableVariablesLoading: false,
    };
  }),

  on(listPostproAvailableVariablesFailure, (state) => {
    return {
      ...state,
      availableVariablesLoading: false,
    };
  }),

  /**
   * Results Actions
   */

  on(
    clearPostproAvailableVariables,
    deselectAllResults,
    (state) => {
     return adapter.removeAll({ ...state, availableVariables: null });
  }),

  // Misc

  on(selectGridOverlay, (state, { gridOverlay }) => {
    return {
      ...state,
      gridOverlay,
    };
  }),

  on(cameraClick, (state, { camera }) => {
    if (state.allowMultipleCameras) {
      const cameraSet = new Set<string>(state.selectedCameras);
      if (cameraSet.has(camera)) {
        cameraSet.delete(camera);
      } else {
        cameraSet.add(camera);
      }

      return {
        ...state,
        selectedCameras: [...cameraSet],
      };
    } else {
      return {
        ...state,
        selectedCameras: [camera],
      };
    }
  }),

  on(toggleAllowMultipleCameras, (state) => {
    const allowMultipleCameras = !state.allowMultipleCameras;
    return {
      ...state,
      allowMultipleCameras,
      selectedCameras: state.selectedCameras.length ? [state.selectedCameras[0]] : [],
    };
  }),

  on(setSelectedCameras, (state, { cameras }) => {
    if (state.allowMultipleCameras) {
      return {
        ...state,
        selectedCameras: cameras,
      };
    } else {
      return {
        ...state,
        selectedCameras: [cameras[0]],
      };
    }
  }),

  on(setPlotOverlayReferences, (state, { plotOverlayReferences }) => {
    return { ...state, plotOverlayReferences };
  }),

  on(clearPlotOverlayReferences, (state) => {
    return { ...state, plotOverlayReferences: [] };
  }),

  on(setPlotOverlayClipping, (state, { plotOverlayClipping }) => {
    return { ...state, plotOverlayClipping };
  }),

  on(setPlotOverlaySide, (state, { plotOverlaySide }) => {
    return { ...state, plotOverlaySide };
  }),

  on(clearPlotOverlaySettings, (state) => {
    return {
      ...state,
      plotOverlayReferences: [],
      plotOverlayClipping: null,
      plotOverlaySide: null,
    };
  }),

  on(loadLICReferencesSuccess, (state, { licReferences }) => {
    return {
      ...state,
      licReferences,
    };
  }),

  on(clearLICReferences, (state) => {
    return {
      ...state,
      licReferences: [],
    };
  })

);

export const featureKey = 'postpro';
export const {
  selectIds: getIds,
  selectEntities: getEntities,
  selectAll: getAll,
  selectTotal: getTotal,
} = adapter.getSelectors();
export const getSelectedVariables = (state: State) => state.selectedVariables;
export const getLoadedVariables = (state: State) => state.loadedVariables;
export const getSelectedType = (state: State) => state.selectedType;
export const getReferencesLoading = (state: State) => state.referencesLoading;
export const getAvailableVariables = (state: State) => state.availableVariables;
export const getAvailableVariablesLoading = (state: State) => state.availableVariablesLoading;
export const getGridOverlay = (state: State) => state.gridOverlay;
export const getSelectedCameras = (state: State) => state.selectedCameras;
export const getAllowMultipleCameras = (state: State) => state.allowMultipleCameras;
export const getPlotOverlayReferences = (state: State) => state.plotOverlayReferences;
export const getPlotOverlayClipping = (state: State) => state.plotOverlayClipping;
export const getPlotOverlaySide = (state: State) => state.plotOverlaySide;
export const getLICOverlayReferences = (state: State) => state.licReferences;
